// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getCostAggregate } from '@/costs/api/get-cost-aggregate';
import { CostFilter } from '@/costs/api/get-costs';
import { CostAggregate } from '@/costs/api/types';
import { FIVE_MINUTES_MS, THIRTY_SECONDS_MS } from '@/utilities/time-constants';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * Hook that fetches cost aggregate data for costs matching the provided filters
 * Uses the same filter interface as the costs endpoint but optimized for aggregation
 */
export function useCostAggregate(
  filter?: Omit<CostFilter, 'pageSize' | 'pageNumber' | 'sort'>
) {
  const queryResult = useQuery({
    queryKey: ['costs', 'aggregate', filter],
    queryFn: () => getCostAggregate(filter),
    staleTime: THIRTY_SECONDS_MS,
    gcTime: FIVE_MINUTES_MS,
  });

  const aggregateData = useMemo<CostAggregate>(() => {
    if (!queryResult.data?.data) {
      // Return empty data structure when no data is available
      return {
        organizationCurrencyTotal: null,
        unconvertedEntries: 0,
        totalEntries: 0,
        byCurrency: [],
      };
    }

    return queryResult.data.data;
  }, [queryResult.data]);

  return {
    aggregateData,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    error: queryResult.error,
  } as const;
}
